import { useQuery } from "@tanstack/react-query";
import { AssetMainType } from "../models/asset";
import moment from "moment";
import { useEffect } from "react";
import { ExchangeRateCommandFactory } from "../commands/exchangeRateFactory";
import useAuthHttp from "./useAuthHttp";
import { isMissingQuote, parseMissingQuoteBody } from "../commons/missing-quote";

export const useExchangeRateByType = (
    assetType: AssetMainType|undefined, 
    date: Date|undefined, 
    code: string|undefined,
    onChange: (cotation: number) => void,
    onError?: (error: Error) => void,
    fundType?: "B3" | "CVM"
) => {
    const authHttp = useAuthHttp();
    const codeWithoutEndSpace = code?.trimEnd();
    
    const exchangeRateQuery = useQuery({
        queryKey: ["exchangeRateQuery", assetType, date, codeWithoutEndSpace, fundType],
        refetchOnWindowFocus: false,
        queryFn: async () => {
            if (!date || !codeWithoutEndSpace || !assetType) {
                return null;
            }

            const dateMoment = moment(date);
            if (dateMoment.day() === 0) {
                dateMoment.subtract(1, 'day');
            }

            try {
                const factory = ExchangeRateCommandFactory();
                const command = factory.getInstance(assetType)(codeWithoutEndSpace, dateMoment.toDate(), authHttp, fundType);
                
                if (!command) {
                    return null;
                }

                const response = await command.executeAsync();
                if (!response) {
                    return null;
                }

                if (isMissingQuote(response)) {
                    return response;
                }

                onChange(response.cotation);

                return response;
            } catch (error) {
                const axiosError = error as { response?: { data?: unknown } };
                const missing = parseMissingQuoteBody(axiosError.response?.data);
                if (missing) {
                    return missing;
                }
                console.error("error", error);
                return null;
            }
        }
    });

    useEffect(() => {
        if (!exchangeRateQuery.isError) {
            return;
        }

        onError?.(exchangeRateQuery.error as Error);
    }, [exchangeRateQuery.isError]);

    return {
        exchangeRateQuery
    };
}